import classnames from 'classnames/bind'
import React from 'react'
import { useInView } from 'react-intersection-observer'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'

import { Spinner } from '@unlikelystudio/react-abstract-components'
import { useIsomorphicLayoutEffect } from '@unlikelystudio/react-hooks'

import ProductCard, { ProductCardProps } from '~/components/UI/ProductCard'
import { CARD_PRODUCT_LAYOUT } from '~/components/UI/ProductCard/types'

import { useStyle } from '~/providers/StyleProvider'

import useTranslatedSmartCount from '~/hooks/useTranslatedSmartCount'

import { SEARCH } from '~/data/dictionary'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type SearchResultsGridProps = {
  className?: string
  results?: ProductCardProps[]
  nbHits: number
  searchQuery: string
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
}

function SearchResultsGrid({
  className,
  results,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  nbHits,
  searchQuery,
}: SearchResultsGridProps) {
  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })
  const titleStyle = useStyle({
    textPreset: GlobalTextPreset.Label12Grotesk,
    textStyling: GlobalTextStyling.UpperCase,
  })
  const [inViewRef, inView] = useInView({
    rootMargin: '0px 0px 600px 0px',
  })

  useIsomorphicLayoutEffect(() => {
    if (inView && hasNextPage) fetchNextPage()
  }, [inView, hasNextPage])

  const COUNT_TRANSLATION = useTranslatedSmartCount(
    [SEARCH.COUNT_SINGLE_RESULT, SEARCH.COUNT_MULTIPLE_RESULTS],
    nbHits,
  )

  return (
    <div className={cx(css.SearchResultsGrid, className)}>
      <div className={cx(gridStyle, css.grid)}>
        <div className={css.gridTop}>
          <h1 className={titleStyle}>
            {`${COUNT_TRANSLATION} "${searchQuery}"`}
          </h1>
        </div>
      </div>
      <div className={cx(gridStyle, css.grid)}>
        {results?.map((result, index) => (
          <div key={`search_result_${index}`} className={css.item}>
            <ProductCard layout={CARD_PRODUCT_LAYOUT.SEARCH_GRID} {...result} />
          </div>
        ))}

        <div className={css.marker} ref={inViewRef}>
          {isFetchingNextPage && (
            <div className={css.spinnerWrapper}>
              <Spinner className={css.spinner} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

SearchResultsGrid.defaultProps = {}

export default SearchResultsGrid
